import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { faqPage, jsonLdScript } from '@/lib/schema'

const URL = 'https://uptrack.app/blog/what-is-uptime-monitoring'
const TITLE = 'What Is Uptime Monitoring? How It Works | Uptrack'
const DESCRIPTION =
  'Uptime monitoring checks your site from outside on a schedule and alerts you when it fails. How it works, what to check, and how often to check.'

const faq = [
  {
    question: 'What is uptime monitoring in simple terms?',
    answer:
      'A service requests your website or API on a fixed schedule from outside your network. If the request fails, the service alerts you. It also records each result, so you can see your uptime over time.',
  },
  {
    question: 'How is uptime calculated?',
    answer:
      'Uptime is the time a service was up, divided by the total time in the period, times 100. A site that was down for 43.8 minutes in a 730-hour month had 99.9% uptime.',
  },
  {
    question: 'How often should a website be checked?',
    answer:
      'Use the shortest interval that you can get for pages that earn money or that customers log in to. A check every 30 seconds finds an outage within 30 seconds. A check every 5 minutes can miss a short outage completely.',
  },
  {
    question: 'Do I need to install anything?',
    answer:
      'No. HTTP, keyword, DNS, SSL, TCP and ping checks run from outside and need only an address. A heartbeat monitor is the exception: your cron job must send a request to a URL each time it runs.',
  },
  {
    question: 'What is the difference between uptime monitoring and APM?',
    answer:
      'Uptime monitoring tests your service from outside and tells you that it failed. APM runs inside your application and helps you find out why. Many teams use both.',
  },
]

export const Route = createFileRoute('/_site/blog/what-is-uptime-monitoring')({
  component: WhatIsUptimeMonitoringPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: TITLE },
      { property: 'og:description', content: DESCRIPTION },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-what-is-uptime-monitoring.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: URL },
      { property: 'article:published_time', content: '2026-11-01' },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'What Is Uptime Monitoring? How It Works and What to Check',
          description: DESCRIPTION,
          datePublished: '2026-11-01',
          dateModified: '2026-11-01',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app' },
          mainEntityOfPage: URL,
        }),
      },
      jsonLdScript(faqPage(faq)),
    ],
  }),
})

const checkTypes = [
  { type: 'HTTP', finds: 'The page returns an error or does not answer', to: '/features/uptime-monitoring' },
  { type: 'Keyword', finds: 'The page answers, but the expected text is missing', to: '/features/keyword-monitoring' },
  { type: 'SSL', finds: 'The certificate is about to expire', to: '/features/ssl-monitoring' },
  { type: 'DNS', finds: 'A DNS record changed or does not resolve', to: '/features/dns-monitoring' },
  { type: 'TCP and ping', finds: 'A port or host does not answer', to: '/features/tcp-monitoring' },
  { type: 'Heartbeat', finds: 'A cron job did not run on time', to: '/features/heartbeat-monitoring' },
  { type: 'Browser', finds: 'A login or checkout flow breaks', to: '/features/browser-monitoring' },
] as const

const intervals = [
  { interval: '30 seconds', slowest: '30 seconds', short: 'Finds it' },
  { interval: '1 minute', slowest: '1 minute', short: 'Can miss it' },
  { interval: '5 minutes', slowest: '5 minutes', short: 'Often misses it' },
  { interval: '30 minutes', slowest: '30 minutes', short: 'Usually misses it' },
]

const related = [
  { name: 'Uptime monitoring', question: 'Is it up from outside, right now?' },
  { name: 'Browser (synthetic) monitoring', question: 'Can a user still finish a login or checkout?' },
  { name: 'APM', question: 'Which code or query inside the application is slow or failing?' },
  { name: 'Status page', question: 'What do we tell customers during an outage?' },
]

const P = 'mb-4 text-lg leading-[1.7] text-body'
const H2 = 'font-sans mb-4 text-2xl font-semibold'
const TH = 'px-4 py-3 text-left text-sm font-semibold'
const TD = 'px-4 py-3 text-sm text-body'

function WhatIsUptimeMonitoringPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">November 1, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">
        What is uptime monitoring? How it works and what to check
      </h1>

      <section className="mb-12">
        <p className={P}>
          Your site can go down while your server dashboard looks normal. An expired certificate, a bad DNS change or a
          failed deploy can all stop your customers. Without a check from outside, the first alert is often a customer
          email.
        </p>
        <p className={P}>
          <strong>
            Uptime monitoring is a service that requests your website or API from outside your network on a fixed
            schedule, records the result, and alerts you when a check fails.
          </strong>{' '}
          It answers one question: can people reach this service right now?
        </p>
      </section>

      <section className="mb-12">
        <h2 className={H2}>How an uptime check works</h2>
        <ol className="mb-4 list-decimal space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li>The monitor requests your address on a schedule, for example every 30 seconds.</li>
          <li>It compares the answer with your rule: a status code, a response time or a word on the page.</li>
          <li>If the check fails, a good service tests again, often from a second location, to reject false alarms.</li>
          <li>It alerts you by email, chat or phone, and records the start of the incident.</li>
          <li>When checks pass again, it closes the incident and records how long the outage lasted.</li>
        </ol>
        <p className={P}>
          You can try one check now. Our{' '}
          <Link to="/tools/is-website-down" className="underline">
            website down checker
          </Link>{' '}
          tests any address from 24 locations, with no account.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={H2}>What you can monitor</h2>
        <p className={P}>Start with HTTP. Add other check types when a failure type has hurt you, or could.</p>
        <div className="mb-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className={TH}>Check</th>
                <th className={TH}>What it finds</th>
              </tr>
            </thead>
            <tbody>
              {checkTypes.map((c) => (
                <tr key={c.type} className="border-b border-border last:border-0">
                  <td className={TD}>
                    <Link to={c.to} className="underline">
                      {c.type}
                    </Link>
                  </td>
                  <td className={TD}>{c.finds}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className={H2}>How often should you check?</h2>
        <p className={P}>
          The interval sets the longest time before you know about a problem. It also decides if you see a short outage
          at all. This table shows the result for an outage of 45 seconds.
        </p>
        <div className="mb-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className={TH}>Interval</th>
                <th className={TH}>Longest wait for a first failed check</th>
                <th className={TH}>Outage of 45 seconds</th>
              </tr>
            </thead>
            <tbody>
              {intervals.map((r) => (
                <tr key={r.interval} className="border-b border-border last:border-0">
                  <td className={TD}>{r.interval}</td>
                  <td className={TD}>{r.slowest}</td>
                  <td className={TD}>{r.short}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={P}>
          The interval also limits what you can measure. A 99.99% target allows about 4.38 minutes of downtime a month,
          so a check every 5 minutes cannot measure it well. See{' '}
          <Link to="/blog/what-is-uptime-sla" className="underline">
            what a good uptime SLA is
          </Link>{' '}
          for each target.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={H2}>Why one location is not always enough</h2>
        <p className={P}>
          A check from one location can fail because of a network problem near that location, while your site is fine.
          A check can also pass there while users in another region cannot reach you. More locations reduce false alarms
          and show regional outages. Read about{' '}
          <Link to="/blog/22-check-locations" className="underline">
            our 24 check locations
          </Link>
          .
        </p>
      </section>

      <section className="mb-12">
        <h2 className={H2}>Uptime monitoring and related tools</h2>
        <div className="mb-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className={TH}>Tool</th>
                <th className={TH}>The question it answers</th>
              </tr>
            </thead>
            <tbody>
              {related.map((r) => (
                <tr key={r.name} className="border-b border-border last:border-0">
                  <td className={TD}>{r.name}</td>
                  <td className={TD}>{r.question}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className={H2}>What uptime monitoring does not do</h2>
        <ul className="mb-4 list-disc space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li>It does not find the cause inside your code. You need logs or APM for that.</li>
          <li>A basic HTTP check does not prove that a login or checkout works. You need a browser check for that.</li>
          <li>It does not fix the outage. It only makes sure that you know about it early.</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className={H2}>How to set it up</h2>
        <ol className="mb-4 list-decimal space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li>List the addresses that matter: home page, login, API health check, checkout.</li>
          <li>Create one HTTP monitor for each address, at the shortest interval that you can get.</li>
          <li>Add a keyword rule to pages that can return 200 with an error message.</li>
          <li>Send alerts to a place that someone reads, such as Slack, Telegram or Discord, and also to email.</li>
          <li>Stop one test service, or point a monitor at a bad address, to confirm that the alert arrives.</li>
        </ol>
      </section>

      <section className="mb-12">
        <h2 className={H2}>Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.question}>
              <h3 className="font-sans mb-1 font-semibold">{f.question}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Start with 30-second checks</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          The free plan has 5 HTTP or keyword monitors at 30-second checks from one location, with no credit card. Paid
          plans start at $3/mo with more locations and a 30-day money-back guarantee.
        </p>
        <Button size="lg" asChild>
          <Link to="/pricing">
            See plans
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </article>
  )
}
